import { z } from 'zod';

import {
  ARTIFACT_STATUS,
  ARTIFACT_TYPES,
  CLAIM_STATUS,
  CLAIM_TYPES,
  CONFIDENTIALITY,
  DECISION_STATUS,
  EVENT_STATUS,
  EVENT_TYPES,
  EVIDENCE_POLARITY,
  RULE_RUN_STATUS,
  AGENT_RUN_STATUS,
  isClaimAccepted,
  type DecisionStatus,
} from '../constants/enums';

export const decisionRoomDisplayStateSchema = z.enum([
  'no_claims',
  'needs_evidence',
  'has_contradictions',
  'ready_for_decision',
  'approved',
  'rejected',
  'cancelled',
]);
export type DecisionRoomDisplayState = z.infer<typeof decisionRoomDisplayStateSchema>;

export const decisionRoomEvidenceRelationStatusSchema = z.enum([
  'candidate',
  'needs_review',
  'accepted',
  'rejected',
  'superseded',
]);
export type DecisionRoomEvidenceRelationStatus = z.infer<
  typeof decisionRoomEvidenceRelationStatusSchema
>;

/**
 * Per-claim weighted evidence balance (technical/11 lines 94-104). Computed from
 * ACCEPTED relations only; candidate/needs_review/rejected/superseded never count.
 *
 *   support_score        = sum(weight where polarity = supports)
 *   contradiction_score  = sum(weight where polarity = contradicts)
 *   confidence_adjustment = -sum(weight where polarity = weakens)
 *   qualification_count   = count(relations where polarity = qualifies)
 *   material_contradiction = contradiction_score >= max(0.35, support_score * 0.4)
 *   ready_without_contradiction =
 *     support_score >= required_support_score
 *     and !material_contradiction
 *     and confidence_score + confidence_adjustment >= required_confidence
 */
export const decisionRoomClaimBalanceSchema = z.object({
  claim_id: z.string().min(1),
  support_score: z.number(),
  contradiction_score: z.number(),
  confidence_adjustment: z.number(),
  qualification_count: z.number().int().min(0),
  material_contradiction: z.boolean(),
  ready_without_contradiction: z.boolean(),
});
export type DecisionRoomClaimBalance = z.infer<typeof decisionRoomClaimBalanceSchema>;

export const decisionRoomReadinessSchema = z.object({
  ready: z.boolean(),
  display_state: decisionRoomDisplayStateSchema,
  total_claims: z.number().int().min(0),
  active_claims: z.number().int().min(0),
  accepted_relations: z.number().int().min(0),
  candidate_relations: z.number().int().min(0),
  needs_review_relations: z.number().int().min(0),
  rejected_relations: z.number().int().min(0),
  superseded_relations: z.number().int().min(0),
  missing_evidence: z.number().int().min(0),
  contradictions: z.number().int().min(0),
  material_contradictions: z.number().int().min(0),
  required_support_score: z.number(),
  requires_no_material_contradiction: z.boolean(),
  claim_balances: z.array(decisionRoomClaimBalanceSchema),
  blockers: z.array(z.string()),
});
export type DecisionRoomReadiness = z.infer<typeof decisionRoomReadinessSchema>;

export const decisionRoomDecisionSchema = z.object({
  id: z.string().min(1),
  artifact_id: z.string().min(1).nullable(),
  project_id: z.string().min(1).nullable(),
  title: z.string().min(1),
  description: z.string().nullable(),
  decision_type: z.string().nullable(),
  status: z.enum(DECISION_STATUS),
  rationale: z.string().nullable(),
  decided_by_user_id: z.string().nullable(),
  requested_by_user_id: z.string().nullable(),
  confidentiality: z.enum(CONFIDENTIALITY),
  decided_at: z.string().nullable(),
  created_at: z.string().min(1),
  updated_at: z.string().min(1),
});

export const decisionRoomArtifactSchema = z.object({
  id: z.string().min(1),
  project_id: z.string().min(1).nullable(),
  artifact_type: z.enum(ARTIFACT_TYPES),
  title: z.string().min(1),
  status: z.enum(ARTIFACT_STATUS),
  primary_context_id: z.string().min(1).nullable(),
  confidentiality: z.enum(CONFIDENTIALITY),
  created_at: z.string().min(1),
  updated_at: z.string().min(1),
});
export type DecisionRoomArtifact = z.infer<typeof decisionRoomArtifactSchema>;

export const decisionRoomClaimSchema = z.object({
  id: z.string().min(1),
  claim_type: z.enum(CLAIM_TYPES),
  package_id: z.string().min(1).nullable(),
  artifact_id: z.string().min(1).nullable(),
  title: z.string().min(1),
  statement: z.string().min(1),
  status: z.enum(CLAIM_STATUS),
  confidence_score: z.number().nullable(),
  created_at: z.string().min(1),
  updated_at: z.string().min(1),
});
export type DecisionRoomClaim = z.infer<typeof decisionRoomClaimSchema>;

export const decisionRoomEvidenceSummarySchema = z.object({
  id: z.string().min(1),
  artifact_id: z.string().min(1).nullable(),
  evidence_type: z.string().min(1),
  title: z.string().min(1),
  polarity: z.enum(EVIDENCE_POLARITY),
  confidence_score: z.number().nullable(),
  weight: z.number().nullable(),
  created_at: z.string().min(1),
});

export const decisionRoomEvidenceRelationSchema = z.object({
  id: z.string().min(1),
  evidence_id: z.string().min(1).nullable(),
  claim_id: z.string().min(1),
  source_type: z.string().min(1),
  source_id: z.string().min(1),
  relation_type: z.enum(EVIDENCE_POLARITY),
  status: decisionRoomEvidenceRelationStatusSchema,
  context_id: z.string().min(1).nullable(),
  confidence_score: z.number().nullable(),
  weight: z.number().nullable(),
  interpretation: z.string().nullable(),
  produced_by_run_id: z.string().min(1).nullable(),
  reviewed_by_user_id: z.string().min(1).nullable(),
  created_at: z.string().min(1),
  updated_at: z.string().min(1),
  evidence: decisionRoomEvidenceSummarySchema.nullable(),
});
export type DecisionRoomEvidenceRelation = z.infer<typeof decisionRoomEvidenceRelationSchema>;

export const decisionRoomTraceSchema = z.object({
  events: z.array(z.object({
    id: z.string().min(1),
    event_type: z.enum(EVENT_TYPES),
    status: z.enum(EVENT_STATUS),
    source: z.string().min(1),
    created_at: z.string().min(1),
  })),
  rule_runs: z.array(z.object({
    id: z.string().min(1),
    event_id: z.string().min(1),
    rule_id: z.string().min(1),
    status: z.enum(RULE_RUN_STATUS),
    started_at: z.string().min(1),
    finished_at: z.string().nullable(),
  })),
  agent_runs: z.array(z.object({
    id: z.string().min(1),
    rule_run_id: z.string().min(1).nullable(),
    skill_id: z.string().min(1).nullable(),
    profile_id: z.string().min(1).nullable(),
    status: z.enum(AGENT_RUN_STATUS),
    output_summary: z.string().nullable(),
    created_at: z.string().min(1),
  })),
});
export type DecisionRoomTrace = z.infer<typeof decisionRoomTraceSchema>;

interface ReadinessClaim {
  readonly id: string;
  readonly status: string;
  readonly confidence_score?: number | null;
}

interface ReadinessRelation {
  readonly claim_id: string;
  readonly relation_type: string;
  readonly status: string;
  readonly weight?: number | null;
}

/**
 * Acceptance policy a caller may pass to raise the readiness bar (technical/11:
 * "Policy may raise `required_support_score` or set
 * `requires_no_material_contradiction=true` for high-risk decisions"). Defaults
 * model the standard decision gate: any positive support, a non-negative
 * confidence floor, and no material contradiction.
 */
export interface DecisionRoomReadinessPolicy {
  /** Minimum aggregate `support_score` an active claim needs. Default `0`. */
  readonly required_support_score?: number;
  /** Minimum `confidence_score + confidence_adjustment`. Default `0`. */
  readonly required_confidence?: number;
  /** When true (default) a material contradiction blocks readiness. */
  readonly requires_no_material_contradiction?: boolean;
}

export interface DecisionRoomReadinessInput {
  readonly decision_status: DecisionStatus;
  readonly claims: readonly ReadinessClaim[];
  readonly evidence_relations: readonly ReadinessRelation[];
  readonly policy?: DecisionRoomReadinessPolicy;
}

/**
 * Relations that satisfy the "has accepted supporting evidence" presence gate
 * that drives `missing_evidence` / `needs_evidence`. `qualifies` records scope
 * conditions and counts as presence here (back-compat) but is NOT positive
 * `support_score` (technical/11: "qualifies … is not positive support").
 */
function isSupportiveRelation(relationType: string): boolean {
  return relationType === 'supports' || relationType === 'qualifies';
}

function relationWeight(relation: ReadinessRelation): number {
  return typeof relation.weight === 'number' && Number.isFinite(relation.weight)
    ? relation.weight
    : 1;
}

/**
 * The claims that are "in scope" for a decision's readiness: legacy `active`
 * claims plus accepted-ladder `accepted` claims. Once a claim accrues onto the
 * accepted ladder (S10 relation acceptance) it must keep counting toward the
 * gate — otherwise promoting the room's only claim would collapse readiness to
 * `no_claims`. `contradicted` stays IN scope: a claim the accepted evidence
 * refutes is precisely why the room is not ready, so it must keep driving the
 * `has_contradictions` display rather than collapsing to `no_claims`.
 * Disposition statuses (`resolved`, `withdrawn`, `rejected`, `superseded`) stay
 * out of scope.
 */
function isInScopeClaim(status: string): boolean {
  return status === 'active' || status === 'contradicted' || isClaimAccepted(status);
}

function terminalDisplayState(status: DecisionStatus): DecisionRoomDisplayState | null {
  switch (status) {
    case 'approved':
    case 'rejected':
    case 'cancelled':
      return status;
    case 'requested':
      return null;
  }
}

/**
 * The material-contradiction threshold (technical/11): a claim's accepted
 * `contradicts` weight is material once it reaches `max(0.35, support * 0.4)`.
 * Below the floor a single low-weight contradiction does NOT block.
 */
export function isMaterialContradiction(
  supportScore: number,
  contradictionScore: number,
): boolean {
  return contradictionScore >= Math.max(0.35, supportScore * 0.4);
}

/**
 * Per-claim weighted evidence balance from ACCEPTED relations only. This is the
 * spec's evidence-balance accrual (technical/11 lines 88-104) — candidate /
 * needs_review / rejected / superseded relations never contribute.
 */
export function computeClaimBalance(
  claim: ReadinessClaim,
  acceptedRelations: readonly ReadinessRelation[],
  policy: Required<DecisionRoomReadinessPolicy>,
): DecisionRoomClaimBalance {
  const own = acceptedRelations.filter((item) => item.claim_id === claim.id);
  let supportScore = 0;
  let contradictionScore = 0;
  let weakenWeight = 0;
  let qualificationCount = 0;
  for (const relation of own) {
    const weight = relationWeight(relation);
    switch (relation.relation_type) {
      case 'supports':
        supportScore += weight;
        break;
      case 'contradicts':
        contradictionScore += weight;
        break;
      case 'weakens':
        weakenWeight += weight;
        break;
      case 'qualifies':
        qualificationCount += 1;
        break;
      default:
        break;
    }
  }
  const confidenceAdjustment = -weakenWeight;
  const materialContradiction = isMaterialContradiction(supportScore, contradictionScore);
  const baseConfidence = typeof claim.confidence_score === 'number' ? claim.confidence_score : 0;
  const readyWithoutContradiction =
    supportScore >= policy.required_support_score &&
    !materialContradiction &&
    baseConfidence + confidenceAdjustment >= policy.required_confidence;
  return {
    claim_id: claim.id,
    support_score: supportScore,
    contradiction_score: contradictionScore,
    confidence_adjustment: confidenceAdjustment,
    qualification_count: qualificationCount,
    material_contradiction: materialContradiction,
    ready_without_contradiction: readyWithoutContradiction,
  };
}

function resolvePolicy(policy?: DecisionRoomReadinessPolicy): Required<DecisionRoomReadinessPolicy> {
  return {
    required_support_score: policy?.required_support_score ?? 0,
    required_confidence: policy?.required_confidence ?? 0,
    requires_no_material_contradiction: policy?.requires_no_material_contradiction ?? true,
  };
}

export function computeDecisionRoomReadiness(
  input: DecisionRoomReadinessInput,
): DecisionRoomReadiness {
  const policy = resolvePolicy(input.policy);
  const activeClaims = input.claims.filter((item) => isInScopeClaim(item.status));
  const accepted = input.evidence_relations.filter((item) => item.status === 'accepted');
  const claimBalances = activeClaims.map((claim) => computeClaimBalance(claim, accepted, policy));

  const supportiveClaimIds = new Set(
    accepted.filter((item) => isSupportiveRelation(item.relation_type)).map((item) => item.claim_id),
  );
  const missingEvidence = activeClaims.filter((item) => !supportiveClaimIds.has(item.id)).length;
  // Back-compat count: every accepted `contradicts` relation, weighted or not.
  const contradictions = accepted.filter((item) => item.relation_type === 'contradicts').length;
  // The blocking count: claims whose accepted contradiction weight is material.
  const materialContradictions = claimBalances.filter((item) => item.material_contradiction).length;
  const supportShortfall = claimBalances.filter(
    (item) => item.support_score < policy.required_support_score,
  ).length;

  const blockers: string[] = [];
  if (activeClaims.length === 0) blockers.push('no active claims');
  if (missingEvidence > 0) blockers.push('accepted supporting evidence missing');
  if (policy.required_support_score > 0 && supportShortfall > 0) {
    blockers.push('required support score not met');
  }
  if (policy.requires_no_material_contradiction && materialContradictions > 0) {
    blockers.push('accepted contradictions present');
  }

  const contradictionBlocks =
    policy.requires_no_material_contradiction && materialContradictions > 0;

  const terminal = terminalDisplayState(input.decision_status);
  const displayState: DecisionRoomDisplayState =
    terminal ??
    (activeClaims.length === 0
      ? 'no_claims'
      : contradictionBlocks
        ? 'has_contradictions'
        : missingEvidence > 0 || supportShortfall > 0
          ? 'needs_evidence'
          : 'ready_for_decision');

  return {
    ready: displayState === 'ready_for_decision' || displayState === 'approved',
    display_state: displayState,
    total_claims: input.claims.length,
    active_claims: activeClaims.length,
    accepted_relations: accepted.length,
    candidate_relations: input.evidence_relations.filter((item) => item.status === 'candidate').length,
    needs_review_relations: input.evidence_relations.filter((item) => item.status === 'needs_review').length,
    rejected_relations: input.evidence_relations.filter((item) => item.status === 'rejected').length,
    superseded_relations: input.evidence_relations.filter((item) => item.status === 'superseded').length,
    missing_evidence: missingEvidence,
    contradictions,
    material_contradictions: materialContradictions,
    required_support_score: policy.required_support_score,
    requires_no_material_contradiction: policy.requires_no_material_contradiction,
    claim_balances: claimBalances,
    blockers,
  };
}

function readinessMatches(actual: DecisionRoomReadiness, expected: DecisionRoomReadiness): boolean {
  return JSON.stringify(actual) === JSON.stringify(expected);
}

function addIssue(ctx: z.RefinementCtx, message: string, path: (string | number)[]): void {
  ctx.addIssue({ code: z.ZodIssueCode.custom, message, path });
}

export const decisionRoomPacketSchema = z
  .object({
    packet_type: z.literal('decision_room'),
    version: z.literal('v3.decision-room.1'),
    generated_at: z.string().min(1),
    decision: decisionRoomDecisionSchema,
    primary_artifact: decisionRoomArtifactSchema.nullable(),
    deliverables: z.array(decisionRoomArtifactSchema),
    claims: z.array(decisionRoomClaimSchema),
    evidence_relations: z.array(decisionRoomEvidenceRelationSchema),
    readiness: decisionRoomReadinessSchema,
    trace: decisionRoomTraceSchema,
    warnings: z.array(z.string()),
  })
  .superRefine((packet, ctx) => {
    if (packet.decision.artifact_id && !packet.primary_artifact) {
      addIssue(ctx, 'primary artifact is required when decision artifact_id is set', ['primary_artifact']);
    }
    if (
      packet.decision.artifact_id &&
      packet.primary_artifact &&
      packet.primary_artifact.id !== packet.decision.artifact_id
    ) {
      addIssue(ctx, 'primary artifact must match decision artifact_id', ['primary_artifact', 'id']);
    }
    for (const [index, item] of packet.deliverables.entries()) {
      if (item.artifact_type !== 'deliverable' && item.artifact_type !== 'outcome_record') {
        addIssue(ctx, 'deliverables must be deliverable or outcome_record artifacts', ['deliverables', index]);
      }
    }
    const claimIds = new Set(packet.claims.map((item) => item.id));
    for (const [index, relation] of packet.evidence_relations.entries()) {
      if (!claimIds.has(relation.claim_id)) {
        addIssue(ctx, 'evidence relation claim_id must be present in packet claims', [
          'evidence_relations',
          index,
          'claim_id',
        ]);
      }
      if (relation.evidence_id && !relation.evidence) {
        addIssue(ctx, 'evidence relation must include evidence summary when evidence_id is set', [
          'evidence_relations',
          index,
          'evidence',
        ]);
      }
    }
    const expected = computeDecisionRoomReadiness({
      decision_status: packet.decision.status,
      claims: packet.claims,
      evidence_relations: packet.evidence_relations,
    });
    if (!readinessMatches(packet.readiness, expected)) {
      addIssue(ctx, 'readiness summary drift', ['readiness']);
    }
  });
export type DecisionRoomPacket = z.infer<typeof decisionRoomPacketSchema>;

export function assertDecisionRoomPacketIntegrity(input: unknown): DecisionRoomPacket {
  return decisionRoomPacketSchema.parse(input);
}

// ── Decision Room write actions ──────────────────────────────────────────────
// The Room is a Lens, but it exposes three mutating actions that route through
// the existing write seams (relation status writes + DecisionService) and are
// AccessGuard-gated at the route. Accepting/rejecting a relation recomputes
// readiness; requesting a decision opens a `requested` decision (technical/11
// line 135: physical status stays requested -> approved | rejected | cancelled).

/** Accept a candidate/needs_review evidence relation so it counts toward gates. */
export const decisionRoomAcceptRelationRequestSchema = z.object({
  relation_id: z.string().min(1),
  reviewed_by_user_id: z.string().min(1).optional(),
  interpretation: z.string().nullable().optional(),
});
export type DecisionRoomAcceptRelationRequest = z.infer<
  typeof decisionRoomAcceptRelationRequestSchema
>;

/** Reject an evidence relation so it stops contributing to readiness. */
export const decisionRoomRejectRelationRequestSchema = z.object({
  relation_id: z.string().min(1),
  reviewed_by_user_id: z.string().min(1).optional(),
  interpretation: z.string().nullable().optional(),
});
export type DecisionRoomRejectRelationRequest = z.infer<
  typeof decisionRoomRejectRelationRequestSchema
>;

/** Open (request) a decision for this room's artifact. */
export const decisionRoomRequestDecisionRequestSchema = z.object({
  title: z.string().min(1),
  decision_type: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  rationale: z.string().nullable().optional(),
  requested_by_user_id: z.string().min(1).optional(),
});
export type DecisionRoomRequestDecisionRequest = z.infer<
  typeof decisionRoomRequestDecisionRequestSchema
>;

/** The action result returned to the caller: the affected ids + fresh readiness. */
export const decisionRoomActionResultSchema = z.object({
  decision_id: z.string().min(1),
  relation_id: z.string().min(1).nullable(),
  requested_decision_id: z.string().min(1).nullable(),
  readiness: decisionRoomReadinessSchema,
});
export type DecisionRoomActionResult = z.infer<typeof decisionRoomActionResultSchema>;
